'use no memo';
import { Table } from '@tanstack/react-table';
import { useState, useEffect, useRef, useMemo } from 'react';
import { getPages } from './utils';
import PaginationControls from './components/PaginationControls';
import GoToPage from './components/GoToPage';
import { SizeControl } from './components/SizeControl';

export interface TablePaginationAppearance {
  arrowTextClassName?: string;
  pageButtonTextClassName?: string;
  pageButtonActiveClassName?: string;
  pageButtonInactiveClassName?: string;
  sizeControlTextClassName?: string;
  /** The page size dropdown's selected value. */
  sizeControlValueClassName?: string;
  dividerClassName?: string;
}

export interface TablePaginationProps<T = unknown> {
  table: Table<T>;
  titleClassName?: string;
  inputClassName?: string;
  /** Optional extra classes forwarded to the `SizeControl` Dropdown root. */
  sizeDropdownClassName?: string;
  appearance?: TablePaginationAppearance;
}

export const TablePagination = <T,>({
  table,
  titleClassName,
  inputClassName,
  sizeDropdownClassName,
  appearance,
}: TablePaginationProps<T>) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const totalPages = table.getPageCount();
  const currentPage = pageIndex + 1;

  const {
    arrowTextClassName,
    pageButtonTextClassName,
    pageButtonActiveClassName,
    pageButtonInactiveClassName,
    sizeControlTextClassName,
    sizeControlValueClassName,
    dividerClassName,
  } = appearance ?? {};

  const [goToPageValue, setGoToPageValue] = useState<string>(String(currentPage));
  const selectRef = useRef<HTMLSelectElement>(null);

  // Update input value when page changes
  useEffect(() => setGoToPageValue(String(currentPage)), [currentPage]);

  const pages = getPages(currentPage, totalPages);

  const handleGoToPage = (value: string) => {
    const page = Number(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      table.setPageIndex(page - 1);
      setGoToPageValue(String(page));
    }
  };

  // Generate a stable ID for the select element
  const selectId = useMemo(() => `page-size-select-${Math.random().toString(36).slice(2, 11)}`, []);

  return (
    <div className="flex w-full flex-col items-center justify-between">
      <div className={`${dividerClassName ?? 'bg-fletchers-gray-110'} h-[1px] w-full`} />
      <div className="mt-4 flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:justify-between">
        {/* Left side: Pagination controls */}
        <PaginationControls
          table={table}
          pages={pages}
          currentPage={currentPage}
          arrowTextClassName={arrowTextClassName}
          pageButtonTextClassName={pageButtonTextClassName}
          pageButtonActiveClassName={pageButtonActiveClassName}
          pageButtonInactiveClassName={pageButtonInactiveClassName}
        />
        {/* Right side: Go to page and page size controls */}
        <div className="flex max-w-full flex-wrap items-center justify-center gap-[12px] pb-1">
          <GoToPage
            totalPages={totalPages}
            goToPageValue={goToPageValue}
            setGoToPageValue={setGoToPageValue}
            handleGoToPage={handleGoToPage}
            titleClassName={titleClassName}
            inputClassName={inputClassName}
          />

          <SizeControl
            pageSize={pageSize}
            selectId={selectId}
            selectRef={selectRef as React.RefObject<HTMLSelectElement>}
            table={table}
            textClassName={sizeControlTextClassName}
            dropdownClassName={sizeDropdownClassName}
            valueClassName={sizeControlValueClassName}
          />
        </div>
      </div>
    </div>
  );
};
